import React, { useState } from "react";
import http from "../../../../services/http";

/**
 * 27/10/2026 (Quân duyệt GĐ1 + GĐ2) — giấy phép 3 tầng, xem backend services/partner/diaChiTungTram.js.
 *   · Bên ký xác minh MỘT lần ở trạm gốc.
 *   · ĐỊA CHỈ xác nhận từng trạm (trên giấy / giấy địa điểm / chứng từ khác) — bản duyệt ngắn.
 *   · Uỷ quyền ký lưu theo bên ký, dùng chung cho mọi trạm.
 */
export type TramCungBenKy = {
	id: number;
	name: string;
	active: boolean;
	address: string | null;
	contractSigner: string | null;
	bankAccountHolders: string[];
	coverage: "primary" | "address_approved" | "licence_uploaded" | "none";
	evidenceType: string | null;
	evidenceRef: string | null;
};
export type NguoiUyQuyen = { name: string; documentRef?: string };

export const LOAI_CHUNG_TU: Array<{ id: string; label: string }> = [
	{ id: "on_licence", label: "Address is on the verified licence" },
	{ id: "location_document", label: "Location document (location certificate, branch certificate, local business permit)" },
	{ id: "other", label: "Other proof (lease, utility bill, tourism licence)" },
];

const NHAN_PHU: Record<TramCungBenKy["coverage"], { t: string; c: string }> = {
	primary: { t: "Holds the verified licence", c: "#067647" },
	address_approved: { t: "Address verified", c: "#067647" },
	licence_uploaded: { t: "Uploaded its own document — open its row to review", c: "#B54708" },
	none: { t: "Covered by this signing party's licence", c: "#067647" },
};

const o: React.CSSProperties = { padding: "6px 9px", border: "1px solid #D0D5DD", borderRadius: 8, fontSize: 12.5 };
const nut = (bg: string, busy: boolean): React.CSSProperties => ({
	border: "none",
	borderRadius: 8,
	padding: "6px 12px",
	background: bg,
	color: "#fff",
	fontWeight: 700,
	fontSize: 12.5,
	cursor: busy ? "wait" : "pointer",
	opacity: busy ? 0.6 : 1,
});

const loiCua = (e: any) => e?.response?.data?.message || "Could not save. Please try again.";

/** Ô tick trong form duyệt đầy đủ: trạm cùng bên ký nào có địa chỉ ghi trên giấy này. */
export function TickTramCungBenKy(props: { siblings: TramCungBenKy[]; chon: number[]; doi: (ids: number[]) => void }) {
	const ds = props.siblings.filter((s) => s.coverage !== "primary");
	if (!ds.length) return null;
	return (
		<div data-testid="licence-covered-stations" style={{ fontSize: 12.5, color: "#344054" }}>
			<strong>Other stations of this signing party</strong> — tick only those whose address is printed on this licence:
			{ds.map((s) => (
				<label key={s.id} style={{ display: "flex", gap: 6, alignItems: "flex-start", marginTop: 4 }}>
					<input
						type="checkbox"
						data-testid={`licence-cover-${s.id}`}
						checked={props.chon.includes(s.id)}
						onChange={(e) =>
							props.doi(e.target.checked ? [...props.chon, s.id] : props.chon.filter((x) => x !== s.id))
						}
						style={{ marginTop: 3 }}
					/>
					<span>
						#{s.id} {s.name} — {s.address || "(no address)"}
						{s.coverage === "address_approved" ? <em style={{ color: "#067647" }}> · already verified</em> : null}
					</span>
				</label>
			))}
		</div>
	);
}

/** Bản duyệt ngắn: xác nhận ĐỊA CHỈ của một trạm (bên ký đã xác minh ở trạm gốc). */
export function DuyetDiaChi(props: { partnerId: number; daDuyet?: boolean; onDone: () => void; gon?: boolean }) {
	const [loai, setLoai] = useState("");
	const [ref, setRef] = useState("");
	const [busy, setBusy] = useState(false);
	const [loi, setLoi] = useState("");
	const gui = async (status: "approved" | "revoked") => {
		setBusy(true);
		setLoi("");
		try {
			await http.patch(`/admin/partners/${props.partnerId}/business-licence-address`, {
				status,
				evidenceType: loai,
				evidenceRef: ref.trim(),
			});
			setLoai("");
			setRef("");
			props.onDone();
		} catch (e: any) {
			setLoi(loiCua(e));
		} finally {
			setBusy(false);
		}
	};
	if (props.daDuyet) {
		return (
			<button type="button" disabled={busy} style={nut("#98A2B3", busy)} onClick={() => gui("revoked")}>
				Revoke address
			</button>
		);
	}
	return (
		<div data-testid={`address-review-${props.partnerId}`} style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", marginTop: props.gon ? 4 : 0 }}>
			<select value={loai} onChange={(e) => setLoai(e.target.value)} style={{ ...o, maxWidth: 320 }}>
				<option value="">How is the address proven…</option>
				{LOAI_CHUNG_TU.map((l) => (
					<option key={l.id} value={l.id}>
						{l.label}
					</option>
				))}
			</select>
			{loai && loai !== "on_licence" ? (
				<input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Document name / number / date" style={{ ...o, flex: "1 1 200px" }} />
			) : null}
			<button type="button" disabled={busy || !loai} style={nut("#067647", busy)} onClick={() => gui("approved")}>
				Verify address
			</button>
			{loi ? <span style={{ color: "#B42318", fontSize: 12.5, flexBasis: "100%" }}>{loi}</span> : null}
		</div>
	);
}

/** Uỷ quyền ký dùng chung theo bên ký (GĐ2): xem, thêm, bỏ. */
export function UyQuyenKy(props: { partnerId: number; ds: NguoiUyQuyen[]; onDone: () => void }) {
	const [ten, setTen] = useState("");
	const [ref, setRef] = useState("");
	const [busy, setBusy] = useState(false);
	const [loi, setLoi] = useState("");
	const gui = async (body: Record<string, string>) => {
		setBusy(true);
		setLoi("");
		try {
			await http.post(`/admin/partners/${props.partnerId}/business-licence-signers`, body);
			setTen("");
			setRef("");
			props.onDone();
		} catch (e: any) {
			setLoi(loiCua(e));
		} finally {
			setBusy(false);
		}
	};
	return (
		<div data-testid="authorised-signers" style={{ marginTop: 10, fontSize: 12.5, color: "#344054" }}>
			{/* 02/11/2026 (Quân): theo quyết định 28/10 người ký thay KHÔNG cần thư uỷ quyền — họ tự cam kết thẩm quyền lúc
			    ký. Thư chỉ bắt buộc với chuỗi nhiều pháp nhân (backend báo lỗi nếu thiếu). */}
			<strong>Authorised signers</strong> (shared by every station of this signing party — they confirm their authority
			when signing; no authority letter needed, except for multi-entity chains):
			{props.ds.length ? (
				<ul style={{ margin: "4px 0", paddingLeft: 18 }}>
					{props.ds.map((x) => (
						<li key={x.name}>
							{x.name} {x.documentRef ? <span style={{ color: "#667085" }}>({x.documentRef})</span> : null}{" "}
							<button
								type="button"
								disabled={busy}
								onClick={() => gui({ removeName: x.name })}
								style={{ border: "none", background: "none", color: "#B42318", cursor: "pointer", fontSize: 12 }}>
								remove
							</button>
						</li>
					))}
				</ul>
			) : (
				<span style={{ color: "#667085" }}> none.</span>
			)}
			<div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
				<input value={ten} onChange={(e) => setTen(e.target.value)} placeholder="Signer name" style={{ ...o, flex: "1 1 180px" }} />
				<input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Authority letter ref (multi-entity chains only)" style={{ ...o, flex: "1 1 180px" }} />
				<button
					type="button"
					disabled={busy || !ten.trim()}
					style={nut("#0F5F9E", busy)}
					onClick={() => gui(ref.trim() ? { name: ten.trim(), documentRef: ref.trim() } : { name: ten.trim() })}>
					Add signer
				</button>
			</div>
			{loi ? <div style={{ color: "#B42318", marginTop: 4 }}>{loi}</div> : null}
		</div>
	);
}

/** Trạm gốc đã duyệt: tình trạng địa chỉ của mọi trạm cùng bên ký + duyệt ngắn ngay tại chỗ. */
export function TramCungBenKyDaDuyet(props: { siblings: TramCungBenKy[]; onDone: () => void }) {
	if (!props.siblings.length) return null;
	return (
		<div data-testid="sibling-stations" style={{ marginTop: 10, fontSize: 12.5, color: "#344054" }}>
			<strong>Other stations of this signing party</strong> — covered by this licence (no address check, SOP v1.1):
			{props.siblings.map((s) => (
				<div key={s.id} style={{ borderTop: "1px solid #EAECF0", padding: "6px 0" }}>
					<div>
						#{s.id} {s.name}
						{s.active ? "" : " (inactive)"} — {s.address || "(no address)"}
					</div>
					<div style={{ color: "#667085" }}>
						Signer: {s.contractSigner || "(empty)"} · Payout account: {s.bankAccountHolders.join(" · ") || "(none)"}
					</div>
					<div style={{ color: NHAN_PHU[s.coverage].c, fontWeight: 600 }}>
						{NHAN_PHU[s.coverage].t}
						{s.coverage === "address_approved" && s.evidenceRef ? ` (${s.evidenceRef})` : ""}
					</div>
				</div>
			))}
		</div>
	);
}
